import * as React from 'react';
import { memo, useState } from 'react';
import { Link } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Unstable_Grid2';
import mantyLogoUrl from 'assets/logo_manty_new.png';
import stagingLogoUrl from 'assets/logo_staging.png';
import { TRANSITION_TIME, makeTransitions } from 'styles/theme';
import { PARTNERS_EMAIL } from 'business/constants';
import { UserRole } from 'business/definitions';
import { ClickAwayListener } from '@mui/material';
import { SwitchButton } from './ui/SwitchButton';
import SupportAlert from './SupportAlert';
import SideBarItems from './SideBarItems';
import SideBarFooterItems from './SideBarFooterItems';
import { SideBarMode, ToggleSideBarMode } from './hooks';
import { sideBarLogoDimensions } from './helpers';
import { IS_STAGING } from 'env';

const StySideBar = styled(Grid, {
    name: 'StySideBar',
})(({ theme }) => ({
    flexShrink: 0, // avoid being "crushed" by the page content
    width: 'inherit', // avoid an extra wide nav bar!
    maxHeight: '100%', // useful on low height windows
    overflowY: 'auto', // useful on low height windows

    // we don't show the side bar when printing the page, on PDF for instance
    // it adds 100% width clickable links
    '@media print': { display: 'none' },

    backgroundColor: theme.palette.primary.main,

    transition: makeTransitions(['max-width', 'min-width']),

    padding: '1.5rem 1rem 0rem 1rem',

    [theme.breakpoints.down('sm')]: {
        padding: '1.5rem 0rem 0rem 0rem',
    },
})) as typeof Grid; // recommended MUI5 fix when styling a component with "component" prop

const StyLogoGroup = styled(Grid, {
    name: 'StyLogoGroup',
})(({ theme }) => ({
    marginBottom: '2.5rem',
    [theme.breakpoints.down('sm')]: { display: 'none' },
    alignSelf: 'center',
}));

const StyLogoImage = styled('img', {
    name: 'StyLogoImage',
    shouldForwardProp: (prop) => prop !== 'hideLogo',
})<{ hideLogo: boolean }>(({ hideLogo }) => ({
    objectFit: 'contain',
    transition: makeTransitions([
        'max-width',
        'min-width',
        'width',
        'margin-right',
    ]),
    ...sideBarLogoDimensions(hideLogo),
    backgroundColor: 'white',
    padding: hideLogo ? 0 : '0.5rem',
    marginRight: hideLogo ? 0 : '1.5rem',
    // hide the alt text if the image source is not found
    display: 'block',
    overflow: 'hidden',
}));

type Props = {
    sideBarMode: SideBarMode;
    expanded: boolean;
    setExpanded: DispatchState<boolean>;
    toggleSideBarMode: ToggleSideBarMode;
    isAuthenticated: boolean;
    isAdmin: boolean;
    logout: () => void;
    client: ClientT;
    homePageUrl: string;
    userRoles: UserRole[];
};

/**
 * This is the main navigation bar on the side of the app.
 */
const SideBar = ({
    sideBarMode,
    expanded,
    setExpanded: setExpandedProp,
    toggleSideBarMode,
    isAuthenticated,
    isAdmin,
    logout,
    client,
    homePageUrl,
    userRoles,
}: Props): React.ReactElement => {
    const [showAlert, setShowAlert] = useState(false);

    const setExpanded = (newExpanded: boolean) => {
        // In the 'always expanded' mode, we never want to collapse the SideBar
        if (!newExpanded && sideBarMode === 'alwaysExpanded') {
            return;
        }
        setExpandedProp(newExpanded);
    };

    const toggleSideBarModeWithResize = () => {
        toggleSideBarMode();
        // Here we call setExpandedProp because were are switching the SideBar mode and
        // we need to change a state cause a rerender of the SideBar.
        setExpandedProp(sideBarMode === 'preferCollapsed');
        // we need this event to resize the dashboards graph that listen to the windows size only
        // we multiply the transition time to make sure the transition is over
        setTimeout(
            () => window.dispatchEvent(new Event('resize')),
            2 * TRANSITION_TIME,
        );
    };

    // on staging we use a special image to show that we are on staging
    const imageUrl = IS_STAGING
        ? stagingLogoUrl
        : client.logoUrl || mantyLogoUrl;

    return (
        <>
            <SupportAlert
                showAlert={showAlert}
                handleClose={() => setShowAlert(false)}
                emailValue={PARTNERS_EMAIL[client.owner] ?? 'contact@manty.eu'}
            />
            <ClickAwayListener
                onClickAway={() => {
                    // This click away listener is here to toggle on and off
                    // the isNavigatingInChildren state.
                    if (sideBarMode === 'preferCollapsed') {
                        if (expanded) {
                            setExpanded(false);
                        }
                    }
                }}
            >
                <StySideBar
                    container
                    wrap="nowrap"
                    direction="column"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    component="nav"
                    position="static"
                >
                    <StyLogoGroup container justifyContent="center">
                        {/* Applying `display: block` ensures the layout behaves correctly when the sidebar is collapsed.
                         * Without this, navigating to Explore causes the collapsed sidebar to overlap the content.
                         * This style prevents that visual glitch by forcing the link to behave as a block-level element. */}
                        <Link to={homePageUrl} style={{ display: 'block' }}>
                            <StyLogoImage
                                hideLogo={!expanded}
                                src={imageUrl}
                                alt="client's logo"
                            />
                        </Link>
                        <SwitchButton
                            isSideBarModeAlwaysExpanded={
                                sideBarMode === 'alwaysExpanded'
                            }
                            toggleSideBarMode={toggleSideBarModeWithResize}
                        />
                    </StyLogoGroup>
                    <SideBarItems
                        sideBarMode={sideBarMode}
                        isExpanded={expanded}
                        setExpanded={setExpanded}
                        isAuthenticated={isAuthenticated}
                        homePageUrl={homePageUrl}
                    />
                    <SideBarFooterItems
                        sideBarMode={sideBarMode}
                        isExpanded={expanded}
                        setExpanded={setExpanded}
                        isAuthenticated={isAuthenticated}
                        client={client}
                        setShowAlert={setShowAlert}
                        logout={logout}
                        isAdmin={isAdmin}
                        userRoles={userRoles}
                    />
                </StySideBar>
            </ClickAwayListener>
        </>
    );
};

export default memo(SideBar);
